import { extract, logger, processValues, svcApi } from '.';
import { Crop, Net } from '@crops';
import ApiMapper from '../constant/ApiMapper';

type TAiFeaturesReq = {
  url?: string;
  ctpvCd?: string;
  sggCd?: string;
  emdCd?: string;
  treeKindCd?: string;
  searchAddr?: string; // 상세 검색 텍스트
  minx?: string;
  miny?: string;
  maxx?: string;
  maxy?: string;
};

type TAiStatReq = {
  ctpvCd?: string;
  sggCd?: string;
  emdCd?: string;
  treeKindCd?: string;
  // searchAddr?: string;
};

type TAiList = {
  pageSize: any;
  pageNo: any;
} & TAiFeaturesReq;

type TCultureStatReq = {
  sggCd?: string;
  emdCd?: string;
  treeKindCd?: string;
  prjId: string;
  gubun: string; // 작물(10), 읍면동(20)
};

const mapper = ApiMapper.CROP;
const CropApi = {
  GetRegYear: async (): Promise<Crop.RegYears[]> => {
    const result = await svcApi.get(mapper.CULTURE.YEAR_LIST);

    return extract(result);
  },
  GeoJson: async ({ url, regionCode }: { url: string; regionCode: string }): Promise<Crop.TGeoJsonEmd | Net.Error> => {
    const result = await svcApi.get(url, {
      params: { regionCode },
    });

    return extract(result);
  },
  AiFeatures: async ({
    url,
    treeKindCd,
    emdCd,
    searchAddr,
    ctpvCd,
    sggCd,
    minx,
    miny,
    maxx,
    maxy,
  }: TAiFeaturesReq): Promise<Crop.TGeoJsonAiFeature | Net.Error> => {
    const result = await svcApi.get(url, {
      params: processValues({
        sggCd,
        ctpvCd,
        treeKindCd,
        emdCd,
        searchAddr,
        minx,
        miny,
        maxx,
        maxy,
      }),
    });

    return extract(result);
  },
  AiStatistics: async ({ctpvCd, sggCd, emdCd, treeKindCd }: TAiStatReq): Promise<Crop.TCultureStat[]> => {
    const result = await svcApi.get(mapper.AI_RESULT.STAT, {
      params: processValues({
        ctpvCd,
        sggCd,
        emdCd,
        treeKindCd,
      }),
    });

    logger(result);
    return extract(result);
  },
  AiDetailList: async ({
    ctpvCd,
    sggCd,
    emdCd,
    treeKindCd,
    pageNo,
    pageSize,
    searchAddr,
  }: TAiList): Promise<Crop.TAiListResponse> => {
    const result = await svcApi.get(mapper.AI_RESULT.LIST, {
      params: processValues({
        ctpvCd,
        sggCd,
        emdCd,
        treeKindCd,
        pageNo,
        pageSize,
        searchAddr,
      }),
    });

    const total = await svcApi.get(ApiMapper.CROP.AI_RESULT.TOTAL, {
      params: processValues({
        ctpvCd,
        sggCd,
        emdCd,
        treeKindCd,
        searchAddr,
      }),
    });

    logger(result);
    logger(total);
    return { data: extract(result), total: extract(total) };
  },
  AiDetail: async ({ url, farmId }: { url: string; farmId: string }): Promise<Crop.TAiDetailResponse[]> => {
    const result = await svcApi.get(url, {
      params: processValues({
        farmMapId: farmId,
      }),
    });

    logger(result);
    return extract(result);
  },
  SearchAddr: async ({ sggCd, emdCd, searchAddr }: { sggCd: string; emdCd: string; searchAddr: string }) => {
    const result = await svcApi.get(ApiMapper.COMMON.SEARCH_ADDR, {
      params: processValues({
        sggCd,
        emdCd,
        searchAddr,
      }),
    });

    logger(result);

    return extract(result);
  },
  CultureStatistics: async ({
    sggCd,
    emdCd,
    treeKindCd,
    prjId,
    gubun,
  }: TCultureStatReq): Promise<Crop.TCultureStatRes[]> => {
    const result = await svcApi.get(ApiMapper.CROP.CULTURE.STAT, {
      params: processValues({
        gubun,
        prjId,
        sggCd,
        emdCd,
        treeKindCd,
      }),
    });

    logger(result);

    return extract(result);
  },
  CultureDetailList: async ({
    prjId,
    sggCd,
    emdCd,
    treeKindCd,
    sameCd,
    searchAddr,
    pageNo,
    pageSize,
  }: Crop.FormData): Promise<Crop.TCultureListResponse> => {
    const result = await svcApi.get(ApiMapper.CROP.CULTURE.LIST, {
      params: processValues({
        prjId,
        sggCd,
        emdCd,
        treeKindCd,
        sameCd,
        searchAddr,
        pageNo,
        pageSize,
      }),
    });

    const total = await svcApi.get(ApiMapper.CROP.CULTURE.TOTAL, {
      params: processValues({
        sggCd,
        emdCd,
        treeKindCd,
        searchAddr,
        prjId,
        sameCd,
      }),
    });

    logger(result);
    logger(total);
    return { data: extract(result), total: extract(total) };
  },
  CultureDetail: async ({ url, farmId }: { url: string; farmId: string }): Promise<Crop.TAiDetailResponse[]> => {
    const result = await svcApi.get(url, {
      params: processValues({
        farmMapId: farmId,
      }),
    });

    logger(result);
    return extract(result);
  },
  CultureReportList: async ({
    userIdnt,
    pnu,
    prjId,
  }: {
    userIdnt: string;
    pnu: string;
    prjId: string;
  }): Promise<Crop.TCultureReportList[]> => {
    /* 신고 내역 */
    const result = await svcApi.get(mapper.CULTURE.REPORT_LIST, {
      params: processValues({
        userIdnt,
        pnu,
        prjId,
      }),
    });
    logger(result);
    return extract(result);
  },
  CultureListExcel: ({ prjId, sggCd, emdCd, treeKindCd, sameCd, searchAddr }: Crop.FormData): Promise<any> => {
    return svcApi.get(mapper.CULTURE.LIST_DOWNLOAD, {
      params: processValues({
        prjId,
        sggCd,
        emdCd,
        treeKindCd,
        sameCd,
        searchAddr,
      }),
      responseType: 'blob',
    });
  },
  CultureStatListExcel: ({ ctpvCd, treeKindCd, sggCd, emdCd }: Crop.FormData): Promise<any> => {
    return svcApi.get(mapper.CULTURE.STAT_DOWNLOAD, {
      params: processValues({
        ctpvCd,
        sggCd,
        emdCd,
        treeKindCd,
      }),
      responseType: 'blob',
    });
  },
  AiListExcel: ({ ctpvCd, sggCd, emdCd, treeKindCd, searchAddr }: TAiFeaturesReq): Promise<any> => {
    return svcApi.get(mapper.AI_RESULT.LIST_DOWNLOAD, {
      params: processValues({
        ctpvCd,
        sggCd,
        emdCd,
        treeKindCd,
        searchAddr,
      }),
      responseType: 'blob',
    });
  },
  AiTransition: async ({
    sggCd,
    emdCd,
    cltvCd,
  }: {
    sggCd: string;
    emdCd: string;
    cltvCd: string;
  }): Promise<Crop.TAiTransitionRes[]> => {
    const result = await svcApi.get(mapper.AI_RESULT.TRANSITION, {
      params: processValues({
        sggCd,
        emdCd,
        cltvCd,
      }),
    });

    logger(result);
    return extract(result);
  },
  CultureTransition: async ({
    sggCd,
    emdCd,
    cltvCd,
  }: {
    sggCd: string;
    emdCd: string;
    cltvCd: string;
  }): Promise<Crop.TCultureTransition[]> => {
    const result = await svcApi.get(mapper.CULTURE.TRANSITION, {
      params: processValues({
        sggCd,
        emdCd,
        cltvCd,
      }),
    });
    logger(result);

    return extract(result);
  },
  DetailImageLst: async ({ pnu, prjId }: { pnu: string; prjId: string }): Promise<any[]> => {
    const result = await svcApi.get(mapper.CULTURE.IMG_LIST, {
      params: processValues({
        pnu,
        prjId,
      }),
    });

    logger(result);
    return extract(result);
  },
};

export default CropApi;
